/**
 * useDjayBubble — Encapsulates Djay's speech-bubble state with a reducer.
 *
 * Audit H5 / P2 (ronde 2 2026): bubble state was previously 3 separate
 * `useState` calls (`bubbleMessage`, `bubbleColor`, `bubbleAutoDismiss`)
 * in FloatingDjay, each updated together in 8+ places. A reducer
 * collapses those into a single, atomic, intentful action.
 *
 * Actions:
 *  - SHOW: set message + color + auto-dismiss in one go
 *  - HIDE: clear message
 *
 * Note: `emotion` is intentionally NOT part of this state — emotion
 * drives the avatar's secondary motion and has different lifecycle
 * (persists between bubbles, used by accessories, etc.).
 */

import { useReducer, useCallback } from 'react'
import type { BubbleColorScheme } from './DjayBubble'

interface BubbleState {
  message: string | null
  color: BubbleColorScheme
  /** 0 means "no auto-dismiss" (persistent until explicitly hidden). */
  autoDismiss: number | undefined
}

type BubbleAction =
  | {
      type: 'SHOW'
      message: string
      color?: BubbleColorScheme
      autoDismiss?: number
    }
  | { type: 'HIDE' }

const INITIAL: BubbleState = {
  message: null,
  color: 'default',
  autoDismiss: undefined,
}

function reducer(state: BubbleState, action: BubbleAction): BubbleState {
  switch (action.type) {
    case 'SHOW':
      return {
        message: action.message,
        color: action.color ?? 'default',
        autoDismiss: action.autoDismiss,
      }
    case 'HIDE':
      return { ...state, message: null }
    default:
      return state
  }
}

export interface UseDjayBubbleReturn {
  message: string | null
  color: BubbleColorScheme
  autoDismiss: number | undefined
  show: (
    message: string,
    color?: BubbleColorScheme,
    autoDismiss?: number,
  ) => void
  hide: () => void
}

export function useDjayBubble(): UseDjayBubbleReturn {
  const [state, dispatch] = useReducer(reducer, INITIAL)
  const show = useCallback(
    (message: string, color?: BubbleColorScheme, autoDismiss?: number) => {
      dispatch({ type: 'SHOW', message, color, autoDismiss })
    },
    [],
  )
  const hide = useCallback(() => {
    dispatch({ type: 'HIDE' })
  }, [])
  return {
    message: state.message,
    color: state.color,
    autoDismiss: state.autoDismiss,
    show,
    hide,
  }
}
